import { useBrokerContext } from '@/app/context';
import { useGroupLifeQuote } from '@/features/quotes/context/group-life-quote.context';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useInsurerCompanies } from '@/features/quotes/hooks/common/use-insurer-companies.hook';
import { useQuoteForm } from '@/features/quotes/hooks/common/use-quote-form.hook';
import { useCaepfLookup } from '@/features/quotes/hooks/group-life/use-caepf-lookup.hook';
import { useCnpjLookup } from '@/features/quotes/hooks/group-life/use-cnpj-lookup.hook';
import { useDiretrixQuoteEngine } from '@/features/quotes/hooks/group-life/use-diretrix-quote-engine.hook';
import {
  CAEPF_NOT_FOUND_MESSAGE,
  resolveCaepfLookupOutcome,
  useCaepfSelection,
} from '@/features/quotes/hooks/common/use-caepf-selection.hook';
import { SelectCaepfModal } from '@/features/quotes/components/select-caepf.modal';
import { CnaeCombobox } from './cnae-combobox.component';
import { formatGroupLifeDocument } from '@/features/quotes/utils/group-life/format';
import {
  getCleanCpfCaepf,
  isValidCpfOrCaepf,
} from '@/features/quotes/utils/cpf-caepf-document.util';
import {
  stipulatorDefaultValues,
  type StipulatorFormValues,
} from '@/features/quotes/schemas/group-life/stipulator.schema';
import {
  CAEPF_ACTIVE_STATUS,
  CNPJ_ACTIVE_REGISTRATION_STATUS_CODE,
  groupLifeService,
  type CaepfEntry,
} from '@/services';
import type { InsurerCompany } from '@/services/insurer-companies';
import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import {
  cleanCnpjOrCpf,
  useDebouncedValue,
  validateCaepf,
  validateCnpj,
  normalizeEmail,
} from '@/shared/utils';
import {
  Button,
  Combobox,
  Field,
  RadioCard,
  Spinner,
  Switch,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { AlertTriangle, ArrowLeft, ArrowLeftRight, ArrowRight, FilePlus2, Loader2, RefreshCcw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

// formOpts é construído dinamicamente dentro do componente para hidratar
// a partir do contexto quando o usuário volta para esta etapa.

type Props = {
  onNext: (policyId?: string, proposalNumber?: string) => void;
  onBack: () => void;
  isViewMode?: boolean;
};

type LookupState = {
  loading: boolean;
  error: string | null;
  statusMessage: { title: string; body: string } | null;
  isActive: boolean;
};

function getInitialLookupState(): LookupState {
  return { loading: false, error: null, statusMessage: null, isActive: true };
}

function buildCnpjBlockedMessage(description: string) {
  return {
    title: 'Cotação bloqueada — situação cadastral irregular',
    body:
      `O CNPJ informado consta como "${description}" na Receita Federal. ` +
      'Para prosseguir, é necessário que esteja com situação ATIVA. Confirme ' +
      'com o cliente a regularização junto à Receita ou utilize outro CNPJ do grupo econômico.',
  };
}

/**
 * O CPF é apenas o ponto de entrada da busca: o documento gravado na cotação
 * precisa ser um CAEPF, porque o produto PME só tem `TipoDocumento` CNPJ (2) ou
 * CAEPF (3). Enquanto o campo tiver 11 dígitos, o avanço fica travado por esta
 * mensagem — que some assim que o corretor escolhe um CAEPF no modal.
 */
const CAEPF_SELECTION_PENDING_MESSAGE =
  'Selecione o CAEPF vinculado a este CPF para continuar.';

function buildCaepfBlockedMessage(status: string) {
  return {
    title: 'Cotação bloqueada — situação cadastral irregular',
    body:
      `O CAEPF informado consta como "${status}" na Receita Federal. ` +
      'Para prosseguir, é necessário que esteja com situação ATIVA. Confirme ' +
      'com o cliente a regularização junto à Receita antes de emitir a cotação.',
  };
}

export function StepStipulator({ onNext, onBack, isViewMode = false }: Props) {
  const { stipulator, setStipulator } = useGroupLifeQuote();
  const initialValues: StipulatorFormValues = stipulator
    ? {
        insuranceType: stipulator.insuranceType,
        isCaepf: stipulator.isCaepf,
        // Re-deriva a máscara a partir dos dígitos crus para não devolver um
        // valor mascarado (possivelmente corrompido) ao use-mask-input, que
        // reembaralharia os números do CNPJ/CAEPF.
        registerNumber: formatGroupLifeDocument(
          stipulator.registerNumber,
          stipulator.isCaepf,
        ),
        responsibleName: stipulator.responsibleName,
        cnaeCode: stipulator.cnaeCode,
        email: stipulator.email,
        phone: stipulator.phone,
        insurerCompanyId: stipulator.insurerCompanyId ?? '',
      }
    : stipulatorDefaultValues;
  const form = useQuoteForm({ defaultValues: initialValues });
  const [lookup, setLookup] = useState<LookupState>(getInitialLookupState);
  // Idades médias da Diretrix (consulta paralela ao Brasil API). Fallback
  // para o que já está no contexto (hidratado do InsureMO após F5).
  const [diretrixAverages, setDiretrixAverages] = useState<{
    averagePartnerAge?: number;
    averageEmployeeAge?: number;
  } | null>(null);

  // Sincroniza o form com o `stipulator` do contexto sempre que o DOCUMENTO
  // canônico mudar — cobre dois casos:
  //  1) Voltar ao step 1 / reload com o contexto repopulado pelo rascunho
  //     local; e
  //  2) A hidratação do InsureMO "curar" o CNPJ depois (ex.: o rascunho local
  //     tinha um valor mascarado/ofuscado e o back devolveu o correto).
  //
  // Comparamos por DÍGITOS e guardamos o último valor sincronizado para não
  // brigar com o que o corretor digita (digitar não altera o contexto, que só
  // muda no submit/hidratação). O documento é sempre re-derivado dos dígitos
  // crus (`formatGroupLifeDocument`) — um valor perfeitamente mascarado não
  // faz o use-mask-input reembaralhar os números.
  const lastSyncedDocRef = useRef<string | null>(null);
  // Documento (CNPJ) que preencheu o CNAE por último — usado para reaplicar a
  // Atividade Principal quando o corretor TROCA o CNPJ, sem sobrescrever o CNAE
  // persistido no reload (ver `onResult` do DocumentWatcher).
  const lastCnaeDocRef = useRef<string | null>(null);
  useEffect(() => {
    if (!stipulator) return;
    // CNPJ pode ser alfanumérico (novo formato RFB) — usar cleanCnpjOrCpf em
    // vez de só dígitos para a comparação preservar letras quando houver.
    const ctxDigits = cleanCnpjOrCpf(stipulator.registerNumber);
    if (!ctxDigits) return;
    const cur = form.state.values;
    const formDigits = cleanCnpjOrCpf(cur.registerNumber);
    if (ctxDigits === formDigits) return; // já em sincronia
    if (ctxDigits === lastSyncedDocRef.current) return; // edição do usuário
    lastSyncedDocRef.current = ctxDigits;
    // Preserva o que o corretor já preencheu nos campos acompanhantes;
    // aplica do contexto só o documento (autoritativo) e os campos vazios.
    form.reset({
      insuranceType: cur.insuranceType || stipulator.insuranceType,
      isCaepf: stipulator.isCaepf,
      registerNumber: formatGroupLifeDocument(
        stipulator.registerNumber,
        stipulator.isCaepf,
      ),
      responsibleName: cur.responsibleName || stipulator.responsibleName,
      cnaeCode: cur.cnaeCode || stipulator.cnaeCode,
      email: cur.email || stipulator.email,
      phone: cur.phone || stipulator.phone,
      insurerCompanyId:
        cur.insurerCompanyId || stipulator.insurerCompanyId || '',
    });
  }, [stipulator, form]);

  // Modal de seleção de CAEPF: consulta por CPF pode devolver vários CAEPFs.
  // O documento gravado é sempre a INSCRIÇÃO do escolhido.
  const caepfSelection = useCaepfSelection({
    onSelect: (entry, consultedCpf) => {
      // Guarda o CPF consultado à parte (o CAEPF sobrescreve o registerNumber);
      // vazio quando o CAEPF foi digitado direto.
      form.setFieldValue('cpf', consultedCpf ?? '');
      if (entry.registrationNumber) {
        form.setFieldValue(
          'registerNumber',
          formatGroupLifeDocument(entry.registrationNumber, true),
        );
        void form.validateField('registerNumber', 'change');
      }
      if (entry.responsibleName) {
        form.setFieldValue('responsibleName', entry.responsibleName);
      }
    },
  });
  const { requestSelection: requestCaepfSelection, resetFor: resetCaepfFor } =
    caepfSelection;

  const { policyId, productId, productCode, setParams } = useQuoteParams();
  const { selectedBroker } = useBrokerContext();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleSubmit = () => {
    void form.validateAllFields('change').then(async errors => {
      const hasErrors = errors && Object.keys(errors).length > 0;
      if (hasErrors) return;
      if (!lookup.isActive) return;
      const values = form.state.values;
      // Idades médias: prioriza a consulta desta sessão (Diretrix); cai no
      // que o contexto já tem (hidratado do InsureMO após F5).
      const averagePartnerAge =
        diretrixAverages?.averagePartnerAge ?? stipulator?.averagePartnerAge;
      const averageEmployeeAge =
        diretrixAverages?.averageEmployeeAge ?? stipulator?.averageEmployeeAge;
      setStipulator({
        insuranceType: values.insuranceType,
        isCaepf: values.isCaepf,
        registerNumber: values.registerNumber,
        responsibleName: values.responsibleName,
        cnaeCode: values.cnaeCode,
        email: normalizeEmail(values.email),
        phone: values.phone,
        insurerCompanyId:
          values.insuranceType === '4' ? values.insurerCompanyId : undefined,
        averagePartnerAge,
        averageEmployeeAge,
      });

      // Persist step 1 in InsureMO (createEx on first submit / saveStep on
      // subsequent submits when policyId already exists).
      if (creating) return;
      setCreating(true);
      setCreateError(null);
      try {
        const insuranceTypeMap: Record<
          string,
          'NEW' | 'RENEWAL' | 'TRANSFER'
        > = {
          '1': 'NEW',
          '3': 'RENEWAL',
          '4': 'TRANSFER',
        };
        if (!policyId) {
          const numericProductId = Number(productId);
          if (!productId || Number.isNaN(numericProductId)) {
            setCreateError(
              'ID do produto não encontrado. Recarregue a tela de seleção de produtos.',
            );
            return;
          }
          const result = await groupLifeService.createQuote({
            productId: numericProductId,
            // productCode vem do registry (URL/contexto). productVersion segue
            // o default usado no residencial. Campos técnicos (techProductCode/
            // techProductId/productElementId) ficam opcionais — o InsureMO
            // resolve a partir do productCode.
            productCode: productCode || '',
            productVersion: '1.0',
            stipulator: {
              isCaepf: values.isCaepf,
              registerNumber: values.registerNumber,
              cpf: values.cpf || undefined,
              responsibleName: values.responsibleName,
              cnaeCode: values.cnaeCode,
              email: normalizeEmail(values.email),
              phone: values.phone,
              insuranceType:
                insuranceTypeMap[String(values.insuranceType ?? '')],
              transferOriginInsurerCode:
                values.insuranceType === '4'
                  ? values.insurerCompanyId
                  : undefined,
              averagePartnerAge,
              averageEmployeeAge,
            },
            agent: selectedBroker
              ? {
                  name: selectedBroker.name,
                  agentCode: selectedBroker.code || '',
                  partyCode: selectedBroker.code || '',
                  advisoryId: selectedBroker.advisoryId || '',
                  registerNumber: selectedBroker.licenseNumber || '',
                }
              : undefined,
          });
          if (!result.success || !result.data) {
            setCreateError(
              result.error ?? 'Falha ao criar a cotação no InsureMO',
            );
            return;
          }
          // Passa o policyId+proposalNumber para o wizard. Ele faz
          // `navigate({to: '/quotes/products/new/$id', params: {id: policyId}, replace: true})`,
          // colocando o policyId na URL — assim refresh preserva a cotação.
          setParams({
            policyId: result.data.policyId,
            id: result.data.policyId,
            proposalNumber: result.data.proposalNumber,
          });
          onNext(result.data.policyId, result.data.proposalNumber);
          return;
        } else {
          const result = await groupLifeService.saveStep({
            policyId,
            step: 'stipulator',
            payload: {
              IdNo: cleanCnpjOrCpf(values.registerNumber),
              // CAEPF: CPF consultado à parte (o back grava em `CPF`, mantendo o CAEPF em NoDocumento/IdNo).
              Cpf: values.cpf ? cleanCnpjOrCpf(values.cpf) : undefined,
              PartyName: values.responsibleName,
              ContactEmail: normalizeEmail(values.email),
              ContactPhone: values.phone,
              // Tipo de documento (boolean — o back traduz para o código do
              // DataTable BrazilIDType: 2=CNPJ, 3=CAEPF).
              IsCaepf: !!values.isCaepf,
              // Atividade principal — campo oficial `CNAE` do PolicyCustomer.
              Cnae: values.cnaeCode ? String(values.cnaeCode) : undefined,
              // Tipo de seguro (1 Novo, 3 Renovação, 4 Transferência) — a
              // regra de UW de idade máxima diferencia Novo de Renovação/
              // Transferência por ele.
              TipoSeguro: values.insuranceType,
              // Código da congênere quando Transferência ('4'); string vazia
              // limpa o campo no InsureMO quando o usuário troca o tipo.
              SeguradoraAnterior:
                values.insuranceType === '4'
                  ? (values.insurerCompanyId ?? '')
                  : '',
              // Idades médias da Diretrix (sócios/funcionários).
              AveragePartnerAge: averagePartnerAge,
              AverageEmployeeAge: averageEmployeeAge,
            },
          });
          if (!result.success) {
            setCreateError(
              result.error ?? 'Falha ao atualizar a cotação no InsureMO',
            );
            return;
          }
        }
        onNext();
      } finally {
        setCreating(false);
      }
    });
  };

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4'>
        <Text className='text-lg font-bold text-alba-black-500'>
          Dados do Seguro
        </Text>
        <Field.Root>
          <Field.Label>Tipo de Seguro</Field.Label>
          <form.AppField
            name='insuranceType'
            children={field => (
              <RadioCard.Root
                value={field.state.value || '1'}
                onValueChange={details => {
                  const next = details.value as '1' | '3' | '4';
                  field.handleChange(next);
                  // Limpa congênere quando deixa de ser Transferência
                  if (next !== '4') {
                    form.setFieldValue('insurerCompanyId', '');
                  }
                }}
                disabled={isViewMode}
              >
                <div className='flex gap-2 flex-wrap'>
                  <RadioCard.Item value='1'>
                    <RadioCard.ItemControl className='flex-row items-center gap-2'>
                      <FilePlus2 className='w-6 h-6' strokeWidth={1.6} />
                      <RadioCard.ItemText>Seguro Novo</RadioCard.ItemText>
                    </RadioCard.ItemControl>
                    <RadioCard.ItemHiddenInput />
                  </RadioCard.Item>
                  <RadioCard.Item value='3'>
                    <RadioCard.ItemControl className='flex-row items-center gap-2'>
                      <RefreshCcw className='w-6 h-6' strokeWidth={1.6} />
                      <RadioCard.ItemText>Renovação</RadioCard.ItemText>
                    </RadioCard.ItemControl>
                    <RadioCard.ItemHiddenInput />
                  </RadioCard.Item>
                  <RadioCard.Item value='4'>
                    <RadioCard.ItemControl className='flex-row items-center gap-2'>
                      <ArrowLeftRight className='w-6 h-6' strokeWidth={1.6} />
                      <RadioCard.ItemText>Transferência</RadioCard.ItemText>
                    </RadioCard.ItemControl>
                    <RadioCard.ItemHiddenInput />
                  </RadioCard.Item>
                </div>
              </RadioCard.Root>
            )}
          />
        </Field.Root>

        <form.Subscribe
          selector={state => state.values.insuranceType}
          children={insuranceType =>
            insuranceType === '4' ? (
              <form.AppField
                name='insurerCompanyId'
                validators={{
                  onChange: ({ value, fieldApi }) => {
                    const isTransfer =
                      fieldApi.form.getFieldValue('insuranceType') === '4';
                    if (!isTransfer) return undefined;
                    return value ? undefined : 'Congênere é obrigatória';
                  },
                }}
                children={field => (
                  <InsurerCompanyComboboxField
                    value={field.state.value}
                    onChange={field.handleChange}
                    error={field.state.meta.errors?.[0]}
                    disabled={isViewMode}
                  />
                )}
              />
            ) : null
          }
        />
      </div>

      <div className='border-t border-alba-gray-200 pt-6 space-y-4'>
        <Text className='text-lg font-bold text-alba-black-500'>
          Dados do Estipulante
        </Text>

        <form.AppField
          name='isCaepf'
          children={field => (
            <div className='flex items-center gap-3'>
              <Switch.Root
                checked={field.state.value}
                onCheckedChange={details => {
                  field.handleChange(details.checked);
                  form.setFieldValue('registerNumber', '');
                  form.setFieldValue('responsibleName', '');
                  form.setFieldValue('cnaeCode', '');
                  setLookup(getInitialLookupState());
                  resetCaepfFor('');
                  void form.validateField('registerNumber', 'change');
                }}
                disabled={isViewMode}
              >
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
                <Switch.Label>É CAEPF?</Switch.Label>
                <Switch.HiddenInput />
              </Switch.Root>
            </div>
          )}
        />

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
          <form.AppField
            name='registerNumber'
            validators={{
              onChange: ({ value, fieldApi }) => {
                const isCaepf = fieldApi.form.getFieldValue('isCaepf');
                if (!value) {
                  return isCaepf
                    ? 'CPF/CAEPF é obrigatório'
                    : 'CNPJ é obrigatório';
                }
                if (isCaepf) {
                  const clean = getCleanCpfCaepf(value);
                  if (clean.length === 14) {
                    return validateCaepf(value) ? undefined : 'CAEPF inválido';
                  }
                  // CPF completo e válido: aguarda a escolha do CAEPF no modal.
                  if (clean.length === 11) {
                    return isValidCpfOrCaepf(value)
                      ? CAEPF_SELECTION_PENDING_MESSAGE
                      : 'CPF inválido';
                  }
                  return 'CPF/CAEPF deve conter 11 ou 14 dígitos';
                }
                return validateCnpj(value) ? undefined : 'CNPJ inválido';
              },
            }}
            children={field => (
              <form.Subscribe
                selector={state => state.values.isCaepf}
                children={isCaepf => (
                  <field.TextField
                    label={isCaepf ? 'CPF/CAEPF' : 'CNPJ'}
                    placeholder={
                      isCaepf
                        ? 'XXX.XXX.XXX-XX ou XXX.XXX.XXX/XXX-XX'
                        : 'XX.XXX.XXX/XXXX-XX'
                    }
                    required
                    maxLength={
                      isCaepf
                        ? FIELD_MAX_LENGTHS.caepf
                        : FIELD_MAX_LENGTHS.cnpj
                    }
                    mask={isCaepf ? 'cpfCaepf' : 'cnpj'}
                    disabled={isViewMode}
                  />
                )}
              />
            )}
          />

          <form.AppField
            name='responsibleName'
            validators={{
              onChange: ({ value, fieldApi }) => {
                const isCaepf = fieldApi.form.getFieldValue('isCaepf');
                if (!value) {
                  return isCaepf
                    ? 'Nome do Responsável é obrigatório'
                    : 'Razão Social é obrigatória';
                }
                return undefined;
              },
            }}
            children={field => (
              <form.Subscribe
                selector={state => state.values.isCaepf}
                children={isCaepf => (
                  <field.TextField
                    label={isCaepf ? 'Nome do Responsável' : 'Razão Social'}
                    placeholder={
                      isCaepf
                        ? 'Nome do titular do CAEPF'
                        : 'Razão social da empresa'
                    }
                    required
                    maxLength={FIELD_MAX_LENGTHS.companyName}
                    disabled={isViewMode || lookup.loading}
                  />
                )}
              />
            )}
          />
        </div>

        <form.AppField
          name='cnaeCode'
          validators={{
            onChange: ({ value }) =>
              value ? undefined : 'Atividade Principal é obrigatória',
          }}
          children={field => (
            <CnaeCombobox
              value={field.state.value}
              onChange={field.handleChange}
              required
              disabled={isViewMode}
              error={field.state.meta.errors?.[0]}
            />
          )}
        />

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
          <form.AppField
            name='email'
            validators={{
              onChange: ({ value }) => {
                if (!value) return undefined;
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                  ? undefined
                  : 'E-mail inválido';
              },
            }}
            children={field => (
              <field.TextField
                label='E-mail'
                lowercase
                placeholder='contato@empresa.com.br'
                maxLength={FIELD_MAX_LENGTHS.email}
                disabled={isViewMode}
              />
            )}
          />
          <form.AppField
            name='phone'
            children={field => (
              <field.TextField
                label='Telefone'
                placeholder='(99) 99999-9999'
                maxLength={FIELD_MAX_LENGTHS.phone}
                mask='phone'
                disabled={isViewMode}
              />
            )}
          />
        </div>

        {/* DocumentWatcher: on-blur lookup (debounced 500ms) */}
        <form.Subscribe
          selector={state => ({
            value: state.values.registerNumber,
            isCaepf: state.values.isCaepf,
          })}
          children={({ value, isCaepf }) => (
            <DocumentWatcher
              value={value}
              isCaepf={isCaepf}
              onResult={result => {
                setLookup(result.lookup);
                if (result.caepfItems?.length && result.doc) {
                  requestCaepfSelection(result.doc, result.caepfItems);
                } else {
                  // Documento diferente (ou campo limpo) libera o modal para
                  // abrir de novo caso o corretor volte ao mesmo CPF.
                  resetCaepfFor(result.doc ?? '');
                }
                if (result.fill) {
                  form.setFieldValue('responsibleName', result.fill.name);
                  // CNAE (Atividade Principal): preenche quando o campo está
                  // VAZIO ou quando o CNPJ MUDOU em relação ao que preencheu o
                  // CNAE por último (o corretor trocou o documento → a atividade
                  // precisa acompanhar o novo CNPJ). No reload, o primeiro
                  // lookup é do MESMO documento hidratado (prev=null + campo
                  // preenchido) → preserva o CNAE persistido no InsureMO, que
                  // pode ter sido escolhido manualmente e diferir do primário da
                  // Brasil API.
                  if (result.fill.cnaeCode) {
                    const doc = result.fill.doc ?? null;
                    const prevDoc = lastCnaeDocRef.current;
                    const cnaeEmpty = !form.state.values.cnaeCode;
                    const docChanged = prevDoc !== null && prevDoc !== doc;
                    if (cnaeEmpty || docChanged) {
                      form.setFieldValue('cnaeCode', result.fill.cnaeCode);
                    }
                    lastCnaeDocRef.current = doc;
                  }
                }
              }}
              onAverages={setDiretrixAverages}
            />
          )}
        />

        {/* Falha de consulta (Brasil API/CAEPF fora do ar): AVISO não-bloqueante
            — o corretor preenche manualmente e prossegue. Tom âmbar para
            diferenciar do bloqueio por situação cadastral (vermelho, abaixo). */}
        {lookup.error && (
          <div className='rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-800'>
            <div className='flex items-start gap-2'>
              <AlertTriangle
                className='w-5 h-5 mt-0.5 shrink-0'
                strokeWidth={1.8}
              />
              <Text className='text-sm'>{lookup.error}</Text>
            </div>
          </div>
        )}

        {lookup.statusMessage && (
          <div className='rounded-md border border-alba-error-red-300 bg-alba-error-red-50 p-4 text-alba-error-red-800'>
            <div className='flex items-start gap-2'>
              <AlertTriangle
                className='w-5 h-5 mt-0.5 shrink-0'
                strokeWidth={1.8}
              />
              <div>
                <Text className='font-semibold text-sm'>
                  {lookup.statusMessage.title}
                </Text>
                <Text className='text-sm mt-1'>{lookup.statusMessage.body}</Text>
              </div>
            </div>
          </div>
        )}
      </div>

      {createError && (
        <div className='rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>
          {createError}
        </div>
      )}
      <div className='flex justify-between border-t border-alba-gray-200 pt-6'>
        <Button variant='ghost' size='large' onClick={onBack}>
          <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <Button
          variant='primary'
          size='large'
          onClick={handleSubmit}
          disabled={
            !lookup.isActive || lookup.loading || isViewMode || creating
          }
        >
          {creating ? 'Salvando…' : 'Próximo'}
          {lookup.loading || creating ? (
            <Loader2 className='w-5 h-5 animate-spin' strokeWidth={1.6} />
          ) : (
            <ArrowRight className='w-5 h-5' strokeWidth={1.6} />
          )}
        </Button>
      </div>

      <SelectCaepfModal
        isOpen={caepfSelection.isOpen}
        onClose={caepfSelection.close}
        onConfirm={caepfSelection.confirm}
        items={caepfSelection.items}
      />
    </div>
  );
}

type DocumentWatcherResult = {
  lookup: LookupState;
  /** `doc` = documento (limpo) que originou o fill — permite ao parent detectar
   *  troca de CNPJ e reaplicar a Atividade Principal. */
  fill?: { name: string; cnaeCode?: string; doc?: string };
  /** CAEPFs vinculados ao CPF consultado — o parent abre o modal de seleção. */
  caepfItems?: CaepfEntry[];
  /** Documento (limpo) que originou a consulta. */
  doc?: string;
};

/** Idades médias da Diretrix (sócios/funcionários). */
type DiretrixAverages = {
  averagePartnerAge?: number;
  averageEmployeeAge?: number;
};

function DocumentWatcher({
  value,
  isCaepf,
  onResult,
  onAverages,
}: {
  value: string;
  isCaepf: boolean;
  onResult: (result: DocumentWatcherResult) => void;
  /** Reporta as idades médias da Diretrix (`null` = sem dado/limpou). */
  onAverages?: (averages: DiretrixAverages | null) => void;
}) {
  // CNPJ alfanumérico (RFB) ou CAEPF numérico (sem letras) — cleanCnpjOrCpf
  // preserva os dois casos. Para CAEPF o resultado equivale a só dígitos.
  const clean = cleanCnpjOrCpf(value);
  // No modo CAEPF o campo aceita CPF (11 díg.) OU CAEPF (14 díg.): o CPF é o
  // ponto de entrada que descobre os CAEPFs vinculados ao titular.
  const isValid = isCaepf
    ? isValidCpfOrCaepf(value)
    : validateCnpj(value) && clean.length === 14;
  const debouncedDoc = useDebouncedValue(isValid ? clean : '', 500);

  const cnpjQuery = useCnpjLookup(!isCaepf && debouncedDoc ? debouncedDoc : undefined);
  const caepfQuery = useCaepfLookup(isCaepf && debouncedDoc ? debouncedDoc : undefined);
  // Consulta EM PARALELO ao Brasil API: Diretrix quote-engine-data (Compliance)
  // — idades médias de sócios/funcionários (best-effort; falha não bloqueia).
  const diretrixQuery = useDiretrixQuoteEngine(
    !isCaepf && debouncedDoc ? debouncedDoc : undefined,
  );

  // Reporta as idades médias quando a Diretrix responde. CAEPF/limpeza do
  // campo → null (sem dado).
  useEffect(() => {
    if (!onAverages) return;
    if (isCaepf || !debouncedDoc) {
      onAverages(null);
      return;
    }
    if (diretrixQuery.isError) {
      // eslint-disable-next-line no-console
      console.warn(
        `[diretrix] falha ao consultar quote-engine-data: ${diretrixQuery.error?.message}`,
      );
      onAverages(null);
      return;
    }
    if (diretrixQuery.data) {
      onAverages({
        averagePartnerAge: Number.isFinite(diretrixQuery.data.averagePartnerAge)
          ? diretrixQuery.data.averagePartnerAge
          : undefined,
        averageEmployeeAge: Number.isFinite(
          diretrixQuery.data.averageEmployeeAge,
        )
          ? diretrixQuery.data.averageEmployeeAge
          : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCaepf, debouncedDoc, diretrixQuery.data, diretrixQuery.isError]);

  // CNPJ lookup
  useEffect(() => {
    if (isCaepf) return;
    if (!debouncedDoc) {
      onResult({ lookup: getInitialLookupState() });
      return;
    }
    if (cnpjQuery.isFetching) {
      onResult({
        lookup: { loading: true, error: null, statusMessage: null, isActive: true },
      });
      return;
    }
    if (cnpjQuery.isError) {
      // Falha na API de terceiros (Brasil API fora do ar / rate-limit) NÃO
      // bloqueia o fluxo: o corretor preenche Razão Social/CNAE manualmente e
      // prossegue (`isActive: true`). Diferente do bloqueio por situação
      // cadastral irregular (statusMessage), que é regra de negócio e bloqueia.
      onResult({
        lookup: {
          loading: false,
          error:
            'Não foi possível consultar o CNPJ na Receita Federal no momento. ' +
            'Você pode preencher a Razão Social e a Atividade Principal manualmente e prosseguir normalmente.',
          statusMessage: null,
          isActive: true,
        },
      });
      return;
    }
    if (cnpjQuery.data) {
      const isActive =
        cnpjQuery.data.registrationStatusCode === CNPJ_ACTIVE_REGISTRATION_STATUS_CODE;
      onResult({
        lookup: {
          loading: false,
          error: null,
          statusMessage: isActive
            ? null
            : buildCnpjBlockedMessage(cnpjQuery.data.registrationStatusDescription),
          isActive,
        },
        fill: {
          name: cnpjQuery.data.legalName,
          cnaeCode: String(cnpjQuery.data.primaryCnaeCode),
          doc: debouncedDoc,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCaepf,
    debouncedDoc,
    cnpjQuery.isFetching,
    cnpjQuery.isError,
    cnpjQuery.data,
  ]);

  // CAEPF lookup
  useEffect(() => {
    if (!isCaepf) return;
    if (!debouncedDoc) {
      onResult({ lookup: getInitialLookupState() });
      return;
    }
    if (caepfQuery.isFetching) {
      onResult({
        lookup: { loading: true, error: null, statusMessage: null, isActive: true },
      });
      return;
    }
    if (caepfQuery.isError) {
      // Mesmo tratamento do CNPJ: falha na consulta NÃO bloqueia — o corretor
      // preenche manualmente e prossegue.
      onResult({
        lookup: {
          loading: false,
          error:
            'Não foi possível consultar o CAEPF na Receita Federal no momento. ' +
            'Você pode preencher os dados manualmente e prosseguir normalmente.',
          statusMessage: null,
          isActive: true,
        },
      });
      return;
    }
    if (caepfQuery.data) {
      const outcome = resolveCaepfLookupOutcome({
        cleanDocument: debouncedDoc,
        items: caepfQuery.data.items,
      });

      // CPF sem CAEPF: aviso âmbar (não-bloqueante como aviso), mas o campo
      // continua exigindo um CAEPF — o PME não tem TipoDocumento CPF.
      if (outcome === 'no-caepf') {
        onResult({
          lookup: {
            loading: false,
            error: CAEPF_NOT_FOUND_MESSAGE,
            statusMessage: null,
            isActive: true,
          },
          doc: debouncedDoc,
        });
        return;
      }

      // CPF com CAEPFs vinculados: o parent abre o modal de seleção. Nada é
      // preenchido aqui — quem decide o documento é a escolha do corretor.
      if (outcome === 'select') {
        onResult({
          lookup: {
            loading: false,
            error: null,
            statusMessage: null,
            isActive: true,
          },
          caepfItems: caepfQuery.data.items,
          doc: debouncedDoc,
        });
        return;
      }

      const status = caepfQuery.data.registrationStatus ?? '';
      const isActive = status.toUpperCase() === CAEPF_ACTIVE_STATUS;
      onResult({
        lookup: {
          loading: false,
          error: null,
          statusMessage: isActive ? null : buildCaepfBlockedMessage(status),
          isActive,
        },
        fill: caepfQuery.data.responsibleName
          ? { name: caepfQuery.data.responsibleName }
          : undefined,
        doc: debouncedDoc,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCaepf,
    debouncedDoc,
    caepfQuery.isFetching,
    caepfQuery.isError,
    caepfQuery.data,
  ]);

  return null;
}

type InsurerCompanyOption = {
  value: string;
  label: string;
};

function buildInsurerCompanyOption(company: InsurerCompany): InsurerCompanyOption {
  const doc = company.cpfCnpj?.trim();
  return {
    value: String(company.id),
    label: doc ? `${company.name} — ${doc}` : company.name,
  };
}

function InsurerCompanyComboboxField({
  value,
  onChange,
  error,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string | { message?: string };
  disabled?: boolean;
}) {
  const { data, isLoading } = useInsurerCompanies();
  const [search, setSearch] = useState('');

  const allOptions = useMemo(
    () => (data ?? []).map(buildInsurerCompanyOption),
    [data],
  );

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allOptions;
    return allOptions.filter(o => o.label.toLowerCase().includes(term));
  }, [allOptions, search]);

  const selected = allOptions.find(o => o.value === value);
  const displayLabel =
    selected?.label ??
    (value && isLoading ? 'Carregando...' : null);

  const errorMessage =
    typeof error === 'string' ? error : error?.message ?? undefined;

  return (
    <RequiredFieldTooltip active>
      <Field.Root invalid={!!errorMessage} disabled={disabled}>
        <Field.Label className='text-alba-black-500'>
          Congênere<span className='text-alba-error-red-500'>*</span>
        </Field.Label>
        <Combobox.Root
          items={filteredOptions}
          value={value ? [value] : []}
          onValueChange={details => onChange(details.value[0] || '')}
          onInputValueChange={details => setSearch(details.inputValue)}
          selectionBehavior='replace'
          disabled={disabled}
        >
          <Combobox.Control>
            <Combobox.Trigger>
              {displayLabel ?? 'Selecione a congênere'}
            </Combobox.Trigger>
          </Combobox.Control>
          <Combobox.Positioner>
            <Combobox.Content>
              <Combobox.Input
                placeholder='Buscar congênere por nome ou CNPJ...'
                autoFocus
              />
              {isLoading ? (
                <div className='py-8 flex items-center justify-center'>
                  <Spinner size='sm' colorScheme='blue' />
                </div>
              ) : (
                <Combobox.List>
                  <Combobox.ItemGroup>
                    <Combobox.ItemGroupLabel>
                      Congêneres
                    </Combobox.ItemGroupLabel>
                    {filteredOptions.map(item => (
                      <Combobox.Item key={item.value} item={item}>
                        <Combobox.ItemText>{item.label}</Combobox.ItemText>
                        <Combobox.ItemIndicator />
                      </Combobox.Item>
                    ))}
                  </Combobox.ItemGroup>
                  {search.trim().length > 0 && <Combobox.Empty />}
                </Combobox.List>
              )}
            </Combobox.Content>
          </Combobox.Positioner>
        </Combobox.Root>
        {errorMessage && <Field.ErrorText>{errorMessage}</Field.ErrorText>}
      </Field.Root>
    </RequiredFieldTooltip>
  );
}

// Re-export type for usage by StipulatorFormValues
export type { StipulatorFormValues };
